/**
 * Every view-as surface enters, reads and leaves the view-as session through this one hook, so the backend audit call,
 * the per-tab store, the cache reset and the reload always happen together and no screen can keep data fetched as
 * the other identity. Whether a session is active comes from the backend echo, never from the store alone.
 */
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { navigateHome } from 'src/lib/view-as/navigate-home';
import { getErrorStatus, readViewAsEcho, type ViewAsStatus } from 'src/lib/view-as/view-as-response';
import { clearViewAs, getRemainingMinutes, getViewAsTarget, setViewAs, VIEW_AS_TTL_MS } from 'src/lib/view-as/view-as-store';
import { useAppControllerGetMeQuery, useMyPermissionsControllerGetMyPermissionsQuery } from 'src/store/leela-api';
import { rtkQueryBase } from 'src/store/rtk-query-base';
import { Logger } from 'src/utils/logger';
import { useViewAsStartMutation, useViewAsStopMutation } from './view-as-api';

export interface UseViewAsResult {
  readonly echo?: ViewAsStatus;
  readonly isActive: boolean;
  readonly remainingMinutes?: number;
  readonly start: (email: string) => Promise<void>;
  readonly stop: () => Promise<void>;
}

/** Reads the echo the backend attaches to `/me` and my-permissions while it honours the view-as header. */
function useViewAsEcho (): ViewAsStatus | undefined {
  const { data: me } = useAppControllerGetMeQuery();
  const { data: permissions } = useMyPermissionsControllerGetMyPermissionsQuery();
  return readViewAsEcho(me) ?? readViewAsEcho(permissions);
}

export function useViewAs (): UseViewAsResult {
  const dispatch = useDispatch();
  const echo = useViewAsEcho();
  const [startViewAs] = useViewAsStartMutation();
  const [stopViewAs] = useViewAsStopMutation();

  const restartApp = useCallback(() => {
    dispatch(rtkQueryBase.util.resetApiState());
    navigateHome();
  }, [dispatch]);

  const start = useCallback(async (email: string) => {
    const status = await startViewAs({ targetEmail: email }).unwrap();
    const startedAt = Date.now();
    setViewAs({ expiresAt: startedAt + VIEW_AS_TTL_MS, mode: status.mode, startedAt, target: status.target });
    restartApp();
  }, [restartApp, startViewAs]);

  const stop = useCallback(async () => {
    const entry = getViewAsTarget();
    if (entry !== undefined) {
      try {
        await stopViewAs({ targetEmail: entry.target }).unwrap();
      } catch (error: unknown) {
        Logger.warn('View-as stop was not recorded; leaving the session locally', { status: getErrorStatus(error) });
      }
    }
    clearViewAs();
    restartApp();
  }, [restartApp, stopViewAs]);

  return {
    echo,
    isActive: echo !== undefined,
    remainingMinutes: getRemainingMinutes(),
    start,
    stop,
  };
}
